import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid2, Typography, Chip, CircularProgress, Button, Divider, IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { SelectField } from 'components';
import VTKDataRefreshForm from './VTKDataRefreshForm';
import VTKDataRefreshEmailForm from './VTKDataRefreshEmailForm';
import VTKDataRefreshOneDriveForm from './VTKDataRefreshOneDriveForm';
import ConfirmDialog from './ConfirmDialog';
import { VTKImportLockStatusAction, ForceReleaseVTKImportLockAction } from '../actions/DataManagementActions';
import { enqueueSnackbar } from 'actions';

// Unifies the three VTK Data Refresh sources (Manual Upload / E-mail / OneDrive) in a single
// screen, since they all share the same VTKImportLock semaphore on the backend - only one of
// them can ever be running at a time, so it makes sense to show that shared state in one place
// instead of three separate, unrelated-looking menu options.
const SOURCE_OPTIONS = [
	{ value: 'Manual', disabled: false, text: 'Manual Upload' },
	{ value: 'Email', disabled: false, text: 'From E-mail' },
	{ value: 'OneDrive', disabled: false, text: 'From OneDrive' },
];

const VTKDataRefreshHub = () => {
	const role = useSelector(state => state.session.user.role);
	const dispatch = useDispatch();

	const [source, setSource] = useState('Manual');
	// null = status not known yet (still loading)
	const [isLocked, setIsLocked] = useState(null);
	const [isCheckingStatus, setIsCheckingStatus] = useState(false);
	const [isReleasing, setIsReleasing] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);

	const checkStatus = useCallback(async () => {
		try {
			setIsCheckingStatus(true);
			const data = await VTKImportLockStatusAction();
			setIsLocked(Boolean(data.isLocked));
		} catch (error) {
			dispatch(enqueueSnackbar({ message: `Could not check the lock status. ${error}`, options: { variant: 'error' } }));
		} finally {
			setIsCheckingStatus(false);
		}
	}, [dispatch]);

	useEffect(() => {
		checkStatus();
	}, [checkStatus]);

	const handleForceRelease = async () => {
		try {
			setIsReleasing(true);
			const data = await ForceReleaseVTKImportLockAction();
			dispatch(enqueueSnackbar({ message: data.message, options: { variant: 'success' } }));
			await checkStatus();
		} catch (error) {
			dispatch(enqueueSnackbar({ message: `The lock could not be released. ${error}`, options: { variant: 'error' } }));
		} finally {
			setIsReleasing(false);
			setConfirmOpen(false);
		}
	};

	const locked = Boolean(isLocked);

	return (
		<Grid2 container spacing={2}>
			<Grid2 item size={12} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
				{isLocked === null ? (
					<CircularProgress size={20} />
				) : (
					<Chip
						label={locked ? 'Locked - an import is running' : 'Free - no import running'}
						color={locked ? 'error' : 'success'}
					/>
				)}
				<Tooltip title="Refresh Status">
					<span>
						<IconButton size="small" onClick={checkStatus} disabled={isCheckingStatus}>
							<RefreshIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			</Grid2>

			<Grid2 item size={12}>
				<SelectField
					id="inputSource"
					name="inputSource"
					value={source}
					onChange={(e) => setSource(e.target.value)}
					hintText="Source"
					menuItems={SOURCE_OPTIONS}
				/>
			</Grid2>

			<Grid2 item size={12}>
				{source === 'Manual' && <VTKDataRefreshForm locked={locked} onAfterSubmit={checkStatus} />}
				{source === 'Email' && <VTKDataRefreshEmailForm locked={locked} onAfterSubmit={checkStatus} />}
				{source === 'OneDrive' && <VTKDataRefreshOneDriveForm locked={locked} onAfterSubmit={checkStatus} />}
			</Grid2>

			{role === 'Administrator' && (
				<React.Fragment>
					<Grid2 item size={12}>
						<Divider sx={{ my: 1 }} />
						<Typography variant="overline" color="text.secondary">
							Administrator
						</Typography>
					</Grid2>
					<Grid2 item size={12}>
						<Typography variant="body2" align="center" sx={{ color: 'text.secondary' }}>
							Only force-release the lock if you are sure no import is actually in progress (e.g. it crashed or got stuck).
						</Typography>
					</Grid2>
					<Grid2 item size={12}>
						<Button
							variant="outlined"
							color="error"
							fullWidth
							onClick={() => setConfirmOpen(true)}
							disabled={isReleasing || isCheckingStatus}
						>
							Force Release Lock
						</Button>
					</Grid2>
				</React.Fragment>
			)}

			<ConfirmDialog
				open={confirmOpen}
				title="Force Release Lock"
				message="Are you sure no VTK import is actually running? Releasing the lock while one is genuinely in progress may let two imports run at the same time and corrupt data."
				onConfirm={handleForceRelease}
				onCancel={() => setConfirmOpen(false)}
				isLoading={isReleasing}
			/>
		</Grid2>
	);
};

export default VTKDataRefreshHub;
